import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, HeaderTitle, Input, Label, Select } from '@atlas/ui';

import { PIX_TYPES, PIX_TYPES_VALUES_MAX_LENGTH } from 'common/constants';
import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

import { displayWithdrawalSuccessNotification } from '../withdrawalUtils';

const optionsPixtype = Object.values(PIX_TYPES).map(item => ({
  text: `withdrawal_pixstarspay_pixtype_${item}`,
  value: item,
}));

const validateEmail = email =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

const validatePixKey = (pixType, value) => {
  if (!value) return { message: 'required' };

  const errorMaxLength = ruleOption => ({ message: 'max_length', params: { ruleOption } });

  switch (pixType) {
    case PIX_TYPES.PHONE:
      if (value.length > PIX_TYPES_VALUES_MAX_LENGTH.PHONE)
        return errorMaxLength(PIX_TYPES_VALUES_MAX_LENGTH.PHONE);
      break;
    case PIX_TYPES.EMAIL:
      if (!validateEmail(value)) return { message: 'incorrect_email' };
      if (value.length > PIX_TYPES_VALUES_MAX_LENGTH.EMAIL)
        return errorMaxLength(PIX_TYPES_VALUES_MAX_LENGTH.EMAIL);
      break;
    case PIX_TYPES.EVP:
      if (value.length > PIX_TYPES_VALUES_MAX_LENGTH.EVP)
        return errorMaxLength(PIX_TYPES_VALUES_MAX_LENGTH.EVP);
      break;
    default:
      return null;
  }
};

export const WithdrawalPixPopup = ({ data, id }) => {
  const { formAction, isNewImplementation, paymentMethod, pmId, redirectUrl } = data;
  const parsedData = useMemo(
    () => parseWithdrawalQuery(redirectUrl, { parseNumbers: false }),
    [redirectUrl],
  );
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pixType, setPixType] = useState();
  const [pixKey, setPixKey] = useState('');
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const handleChangePixType = useCallback(
    value => {
      setPixType(value);

      let newPixKey = parsedData[value] || '';

      if (value === PIX_TYPES.PHONE) newPixKey = `+${newPixKey.trim()}`;

      setError(null);
      setPixKey(newPixKey);
    },
    [parsedData],
  );

  const handleChangePixKey = useCallback(
    e => {
      let value = e.target.value.trim();

      if (pixType === PIX_TYPES.PHONE) value = value ? `+${value.replace(/\D/g, '')}` : '';

      const error = validatePixKey(pixType, value);

      setError(error);
      setPixKey(value);
    },
    [pixType],
  );

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: formAction,
    });

    const { amount, currency, paymentId } = parsedData;

    const requestData = {
      amount,
      currency,
      ...(!isNewImplementation
        ? {
            paymentId,
            pixKey,
            pixType,
          }
        : {
            additionalParameters: {
              pixKey,
              pixKeyType: pixType,
              publicPaymentId: paymentId,
            },
            pmId,
          }),
    };

    setIsSubmitting(true);

    try {
      const response = await request(requestData);
      displayWithdrawalSuccessNotification({ amount, currency, paymentMethod, response });
    } catch (error) {
      NotificationManager.display({
        data: {
          headerTitle: 'withdrawal_by_pix',
          title: 'failed_sad_emoticon',
        },
        type: 'error',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    formAction,
    parsedData,
    isNewImplementation,
    pixKey,
    pixType,
    pmId,
    paymentMethod,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className="withdrawal-pix"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big withdrawal-pix__title">
        <PaymentMethodItem
          amount={parseInt(parsedData.amount)}
          paymentMethod={paymentMethod}
          showAmount
        />
      </HeaderTitle>

      <div className="withdrawal-pix__content">
        <Select
          active={isActiveSelect}
          className="withdrawal-pix__pixtype-select"
          label="withdrawal_pixstarspay_pixtype"
          onBlur={() => setIsActiveSelect(false)}
          onChange={handleChangePixType}
          onFocus={() => setIsActiveSelect(true)}
          options={optionsPixtype}
          translateOptions
        />

        <Input
          active={isActiveInput}
          disabled={pixType === PIX_TYPES.CPF}
          error={error}
          label={`withdrawal_pixstarspay_pixtype_${pixType}`}
          onBlur={() => setIsActiveInput(false)}
          onChange={handleChangePixKey}
          onFocus={() => setIsActiveInput(true)}
          value={pixKey}
        />

        {error && (
          <div className="help-text-container">
            <Label message={error.message} params={error.params} />
          </div>
        )}

        <Button
          className="withdrawal-pix__button"
          disabled={!pixKey || error || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalPixPopup.propTypes = {
  data: PropTypes.shape({
    formAction: PropTypes.string,
    isNewImplementation: PropTypes.bool,
    paymentMethod: PropTypes.string,
    pmId: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
